import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useRef } from 'react';
import {
  EMPTY,
  filter,
  map,
  merge,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';

import { useDynamicLessonStateContext } from '../../../../hooks/useCreateLessonState';
import { ErrorDTO } from '../../../../hooks/useDesignerUIState';
import { useMetrics } from '../../../../hooks/useMetrics';
import { SocketContext } from '../../../../providers/SocketProvider/SocketProvider';
import backendService from '../../../../services/backendService';
import { LLMMessageDTO } from '../../../../services/backendService/backendService';
import { AgentState } from '../../../_organisms/DesignerTabPane/AgentTab/AgentHooks';
import { isToolCall } from '../intent-refinement/use-agent-history-messages';

export interface PromptVariables {
  learningObjective: string;
  grade: string;
  subject?: string;
  subjectCategory?: 'ela' | 'math' | 'science' | 'socialStudies' | 'other';
  languages?: string[];
  lessonCategory?:
    | 'teachSomethingNew'
    | 'reviewOrReteach'
    | 'practiceASkill'
    | 'readAloud'
    | 'closeReading'
    | 'other';
  standardAlignment?: string;
  attachments?: { fileUploadId: string }[];
}

export interface LearningObjectiveAgentState {
  // General agent state
  isStreaming: boolean;
  state: 'thinking' | 'generating' | null;
  finishReason: 'completed' | 'cancelled' | 'error' | null;
  error: ErrorDTO | null;
  tokens: string[];
  currentContent: string;
  isComplete: boolean;

  // Learning objective specific state
  isObjectiveReady: boolean;
  promptVariables: PromptVariables | null;
  llmThreadId: string | null;
}

export const useLearningObjectiveRefinement = () => {
  const { gameAPI } = useContext(SocketContext);
  const cleanupRef = useRef<(() => void) | null>(null);

  const isStreaming = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.isStreaming,
  );
  const llmThreadId = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.llmThreadId,
  );

  const dispatch = useDynamicLessonStateContext(c => c.dispatchAndSync);
  const isConnected = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.isConnected,
  );

  const metrics = useMetrics();

  const { refetch } = useQuery(
    ['agentHistory', llmThreadId],
    () =>
      backendService.instance.agent.retrieveLearningObjectiveHistory(
        llmThreadId || '__NONE__',
      ),

    {
      onSuccess: data => {
        dispatch({
          type: 'SET_AGENT_HISTORY_FROM_SERVER',
          payload: data.messages,
        });
      },
      cacheTime: 1000 * 60 * 5,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      enabled: !!llmThreadId,
    },
  );

  useEffect(() => {
    if (gameAPI) {
      if (gameAPI.getSocket().connected && !isConnected) {
        dispatch({
          type: 'SET_AGENT_CONNECTED',
          payload: true,
        });

        dispatch({ type: 'CLEAR_AGENT_ERROR' });
      }
      //Establish listeners for connect and disconnect events
      const connectObservable = gameAPI.createEventObservable('connect').subscribe(() => {
        dispatch({
          type: 'SET_AGENT_CONNECTED',
          payload: true,
        });
        dispatch({
          type: 'CLEAR_AGENT_ERROR',
        });
      });
      const disconnectObservable = gameAPI
        .createEventObservable('disconnect')
        .subscribe(() => {
          dispatch({
            type: 'SET_AGENT_CONNECTED',
            payload: false,
          });
          dispatch({
            type: 'SET_AGENT_STREAM_ERROR',
            payload: {
              error: {
                code: 500,
                message: 'Disconnected',
                type: 'DisconnectError',
              },
            },
          });
        });

      return () => {
        connectObservable.unsubscribe();
        disconnectObservable.unsubscribe();
      };
    }
  }, [dispatch, gameAPI, isConnected]);

  const startStream = useCallback(
    ({
      message,
      attachments,
      historyLength,
    }: {
      message: string;
      attachments?: { fileUploadId: string }[];
      historyLength: number;
    }) => {
      if (isStreaming) {
        return;
      }

      if (!gameAPI) {
        return;
      }

      // Clean up any existing subscription before starting a new one
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      dispatch({
        type: 'SET_AGENT_STREAM_STARTED',
      });

      // Only take the first connection event, then unsubscribe
      const streamObservable = gameAPI.ensureConnected().pipe(
        take(1),
        tap(() => {
          // Little clojure hack to get the message and history length for metrics in the callback
          const metricMessage = message;
          const metricHistoryLength = historyLength;
          // Emit learning objective message event (this is the correct event name)
          gameAPI.getSocket().emit(
            'intent-refinement:message',
            {
              message,
              llmThreadId,
              attachments,
            },
            (res: {
              status: number;
              error?: { status: number; message: string };
              data?: { message: string; sessionId: string; llmThreadId: string };
            }) => {
              if (res.error) {
                dispatch({
                  type: 'SET_AGENT_STREAM_ERROR',
                  payload: {
                    error: {
                      code: res.error.status,
                      message: res.error.message,
                      type: 'error',
                    },
                  },
                });
              }

              // Store this immediately so we can use it in the next message
              if (res.data?.llmThreadId) {
                dispatch({
                  type: 'SET_AGENT_THREAD_ID',
                  payload: res.data?.llmThreadId,
                });
                metrics.logEvent('CreatePage.ObjectiveRefinement.SubmitPrompt', {
                  lessonPrompt: metricMessage,
                  chatHistoryLength: metricHistoryLength,
                  llmThreadId: res.data?.llmThreadId,
                });
              }
            },
          );
        }),
        switchMap(() => {
          let isSuccessfullyCompleted = false;
          const disconnectObservable = gameAPI
            .createEventObservable<unknown>('disconnect')
            .pipe(
              switchMap(() =>
                isSuccessfullyCompleted
                  ? EMPTY // Don't error if already successfully completed
                  : throwError(() => new Error('DISCONNECTED')),
              ),
            );

          // Create observables for each event type
          const tokenObservable = gameAPI
            .createEventObservable<string>('stream:onToken')
            .pipe(map(tokenData => ({ type: 'token' as const, data: tokenData })));

          const streamingStateObservable = gameAPI
            .createEventObservable<AgentState>('stream:state')
            .pipe(map(tokenData => ({ type: 'streamState' as const, data: tokenData })));

          const assistantMessageAddedObservable = gameAPI
            .createEventObservable<{
              content: string;
              isObjectiveReady?: boolean;
              llmThreadId?: string;
              promptVariables?: PromptVariables;
            }>('stream:assistantMessageAdded')
            .pipe(
              map(messageData => ({
                type: 'assistantMessageAdded' as const,
                data: messageData,
              })),
            );

          const errorObservable = gameAPI
            .createEventObservable<{
              error: {
                status: number;
                name: string;
                type: string;
                errors?: { message: string }[];
              };
            }>('stream:error')
            .pipe(
              map(errorData => ({
                type: 'error' as const,
                data: errorData,
              })),
              switchMap(errorEvent => throwError(() => errorEvent.data.error)),
            );

          const stopConditionObservable = streamingStateObservable.pipe(
            filter(event => event.data.finishReason !== null),
            tap(() => {
              isSuccessfullyCompleted = true; // Mark as successfully completed
            }),
            take(1),
          );

          // Merge all observables and complete when the complete event fires
          return merge(
            tokenObservable,
            streamingStateObservable,
            assistantMessageAddedObservable,
            errorObservable,
            disconnectObservable,
          ).pipe(
            takeUntil(stopConditionObservable.pipe(take(1))), // Complete when complete event fires
          );
        }),
      );

      const subscription = streamObservable.subscribe({
        next: event => {
          if (event.type === 'token') {
            dispatch({
              type: 'SET_AGENT_STREAM_ON_TOKEN_RECEIVED',
              payload: { token: event.data },
            });
          }

          if (event.type === 'streamState') {
            dispatch({
              type: 'SET_AGENT_STREAM_ON_STATE_CHANGE',
              payload: event.data,
            });
          }

          // skip tool calls
          if (event.type === 'assistantMessageAdded' && event.data.content) {
            // Handle the completed message
            dispatch({
              type: 'SET_AGENT_ON_COMPLETE_MESSAGE_RECEIVED',
              payload: {
                message: {
                  role: 'ASSISTANT',
                  content: event.data.content,
                  createdAt: new Date().toISOString(),
                  id: 'unknown',
                  model: 'o3',
                  modifiedAt: new Date().toISOString(),
                  threadId: event.data.llmThreadId || 'unknown',
                },
              },
            });

            // Handle objective ready state if provided
            if (event.data.isObjectiveReady !== undefined) {
              dispatch({
                type: 'SET_AGENT_IS_OBJECTIVE_READY',
                payload: event.data.isObjectiveReady,
              });
            }

            // Handle prompt variables if provided
            if (event.data.promptVariables) {
              dispatch({
                type: 'SET_AGENT_PROMPT_VARIABLES',
                payload: event.data.promptVariables,
              });
            }

            // Update thread ID if provided
            if (event.data.llmThreadId) {
              dispatch({
                type: 'SET_AGENT_THREAD_ID',
                payload: event.data.llmThreadId,
              });
            }
          }
        },
        error: (err: {
          error: {
            status: number;
            name: string;
            type: string;
            errors?:
              | {
                  message: string;
                }[]
              | undefined;
          };
        }) => {
          dispatch({
            type: 'SET_AGENT_STREAM_ERROR',
            payload: {
              error: {
                code: err?.error?.status,
                message: err?.error?.name,
                type: err?.error?.type,
                errors: err?.error?.errors,
              },
            },
          });
        },
        complete: () => {
          dispatch({
            type: 'SET_AGENT_STREAM_ON_COMPLETE',
            payload: {
              isComplete: true,
            },
          });
        },
      });

      // Create cleanup function
      const cleanup = () => {
        subscription.unsubscribe();
        cleanupRef.current = null;
      };

      // Store cleanup function in ref and return it
      cleanupRef.current = cleanup;
      return cleanup;
    },
    [dispatch, gameAPI, isStreaming, llmThreadId, metrics],
  );

  const resetStream = useCallback(() => {
    dispatch({ type: 'RESET_AGENT' });
  }, [dispatch]);

  const stopStream = useCallback(() => {
    // Clean up active subscription
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    if (gameAPI) {
      gameAPI.getSocket().emit('agent:stream:stop');
    }
    resetStream();
  }, [gameAPI, resetStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  useEffect(() => {
    if (!gameAPI) {
      return;
    }

    // Create the observable to listen for lesson modifications
    const observable = gameAPI.ensureConnected().pipe(
      switchMap(() => {
        return gameAPI.createEventObservable<LLMMessageDTO>(
          'stream:assistantMessageAdded',
        );
      }),
    );

    const subscription = observable.subscribe({
      next: llmMessage => {
        // don't update for tool calls
        if (!isToolCall(llmMessage)) {
          dispatch({
            type: 'SET_AGENT_ON_COMPLETE_MESSAGE_RECEIVED',
            payload: { message: llmMessage },
          });
          refetch();
        }
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [gameAPI, dispatch, refetch]);

  return {
    startStream,
    resetStream,
    stopStream,
  };
};
